import {
  Container,
  Icon,
  IconButton,
  PremiumFeature,
  SeverityBadge,
  Tag,
  Text,
  Tooltip,
} from "@fluidattacks/design";
import type { CellContext } from "@tanstack/react-table";
import dayjs from "dayjs";
import { capitalize, toUpper } from "lodash";
import _ from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { getOriginIcon } from "features/vulnerabilities/utils";
import { TreatmentTag } from "pages/group/findings/findings-table/styles";
import { getTagVariant } from "utils/colors";
import { formatTreatment } from "utils/format-helpers";
import { msgError, msgSuccess } from "utils/notifications";
import { translate } from "utils/translations/translate";

const LocationFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element | string => {
  const { t } = useTranslation();
  const vuln: IVulnRowAttr = cell.row.original;
  const daysFromReport = dayjs().diff(vuln.reportDate, "days");
  const DAYS_IN_A_WEEK = 7;

  const isNew = daysFromReport <= DAYS_IN_A_WEEK && vuln.state === "VULNERABLE";
  const text = vuln.advisories
    ? `${vuln.where} (${vuln.advisories.cve[0]}, ${vuln.advisories.package}, ${vuln.advisories.vulnerableVersion})`
    : vuln.where;

  const customTag = undefined;
  const handleCopy = useCallback(
    async (ev: React.SyntheticEvent): Promise<void> => {
      ev.stopPropagation();
      const { clipboard } = navigator;
      const currentUrl = `${window.location.href}/${vuln.id}`;
      if (_.isUndefined(clipboard)) {
        msgError(t("searchFindings.copyUrl.failed"));
      } else {
        await clipboard.writeText(currentUrl);
        msgSuccess(
          t("searchFindings.copyUrl.success"),
          t("searchFindings.copyUrl.successTitle"),
        );
      }
    },
    [t, vuln.id],
  );

  return (
    <Container
      {...{ "data-private": true }}
      display={"inline-block"}
      width={"100%"}
    >
      <Container
        alignItems={"center"}
        display={"flex"}
        gap={0.25}
        justify={"space-between"}
        width={"100%"}
      >
        <Container alignItems={"center"} display={"flex"} gap={0.25}>
          {customTag}
          <Text color={"#212a36"} display={"inline-block"} size={"md"}>
            {text}
          </Text>
          {isNew && (
            <Tag
              priority={"medium"}
              tagLabel={translate.t("formatters.includeTags.new")}
              variant={"error"}
            />
          )}
        </Container>
        <IconButton
          icon={"copy"}
          iconSize={"sm"}
          iconType={"fa-light"}
          onClick={handleCopy}
          tooltip={t("searchFindings.copyUrl.tooltip")}
          variant={"ghost"}
        />
      </Container>
    </Container>
  );
};

const OriginFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const theme = useTheme();

  return (
    <Container alignItems={"center"} display={"flex"} gap={0.25}>
      <Icon
        icon={getOriginIcon(cell.row.original.origin)}
        iconSize={"sm"}
        iconType={"fa-light"}
      />
      <Text color={theme.palette.gray[800]} size={"md"}>
        {capitalize(cell.row.original.origin)}
      </Text>
    </Container>
  );
};

const SpecificFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element | string => {
  return (
    <Container {...{ "data-private": true }} display={"inline-block"}>
      <Container alignItems={"center"} display={"flex"} gap={0.25}>
        <Text color={"#212a36"} display={"inline-block"} size={"md"}>
          {cell.row.original.specific}
        </Text>
      </Container>
    </Container>
  );
};

const StatusFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    REJECTED: translate.t("searchFindings.tabVuln.rejected"),
    SAFE: translate.t("searchFindings.tabVuln.closed"),
    SUBMITTED: translate.t("searchFindings.tabVuln.submitted"),
    VULNERABLE: translate.t("searchFindings.tabVuln.open"),
  };
  const status = labels[cell.getValue<string>()];
  const formatedStatus: string = capitalize(status);
  const currentStateVariant = getTagVariant(capitalize(status));
  const tagText =
    formatedStatus === "On_hold"
      ? t("searchFindings.tabVuln.onHold")
      : formatedStatus.split(" ")[0];

  return (
    <Tag
      priority={currentStateVariant === "inactive" ? "default" : "low"}
      tagLabel={tagText}
      variant={currentStateVariant}
    />
  );
};

const SeverityFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const severity = Number(cell.getValue());
  const score = severity.toFixed(1);

  const getVariant = (
    value: number,
  ): "critical" | "none" | "high" | "low" | "medium" => {
    const criticalBound = 9;
    const highBound = 7;
    const mediumBound = 4;

    if (value >= criticalBound) return "critical";
    if (value >= highBound) return "high";
    if (value >= mediumBound) return "medium";
    if (value > 0) return "low";

    return "none";
  };

  const variant = getVariant(severity);

  return (
    <SeverityBadge
      textL={score}
      textR={capitalize(variant)}
      variant={variant === "none" ? "disable" : variant}
    />
  );
};

const PriorityFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
  groupTotalPriority: number,
): JSX.Element => {
  const MIN_PRIORITY = 0.001;
  const { priority, state } = cell.row.original;
  const isVulnerable = state === "VULNERABLE";
  const value = priority / (groupTotalPriority || -1);

  const defaultValue = isVulnerable ? "0.1%" : "0.0%";

  return (
    <div>
      {value <= MIN_PRIORITY ? defaultValue : `${(value * 100).toFixed(2)}%`}
    </div>
  );
};

const TechniqueFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
  isAdvanced: boolean,
): JSX.Element => {
  const labels: Record<string, string> = {
    CSPM: translate.t("searchFindings.tabVuln.technique.cspm"),
    DAST: translate.t("searchFindings.tabVuln.technique.dast"),
    PTAAS: translate.t("searchFindings.tabVuln.technique.ptaas"),
    RE: translate.t("searchFindings.tabVuln.technique.re"),
    SAST: translate.t("searchFindings.tabVuln.technique.sast"),
    SCA: translate.t("searchFindings.tabVuln.technique.sca"),
    SCR: translate.t("searchFindings.tabVuln.technique.scr"),
  };

  const technique = cell.getValue<string>();
  const formatedTechnique: string = toUpper(labels[technique]);

  const advancedTechniques = ["RE", "SCR", "PTAAS"];

  return (
    <Container alignItems={"center"} display={"flex"} gap={0.25}>
      <Tag tagLabel={formatedTechnique} variant={"inactive"} />
      {advancedTechniques.includes(technique) && !isAdvanced && (
        <PremiumFeature />
      )}
    </Container>
  );
};

const TreatmentFormatter = ({
  originalTreatment,
  treatmentAcceptanceStatus,
}: {
  originalTreatment: string;
  treatmentAcceptanceStatus: string | null;
}): JSX.Element => {
  const theme = useTheme();
  const treatment = formatTreatment(originalTreatment);
  const isPendingApproval = treatmentAcceptanceStatus === "SUBMITTED" || false;

  return (
    <Container alignItems={"center"} display={"flex"} gap={0.25}>
      <TreatmentTag $variant={originalTreatment.toLowerCase()}>
        <i className={"fa-solid fa-circle fa-2xs"} />
        {treatment}
      </TreatmentTag>
      {isPendingApproval && (
        <Tooltip
          height={"14px"}
          id={"pending-approval"}
          place={"top"}
          tip={"Pending approval"}
        >
          <Icon
            clickable={true}
            hoverColor={theme.palette.warning[600]}
            icon={"circle-exclamation"}
            iconColor={theme.palette.warning[500]}
            iconSize={"lg"}
          />
        </Tooltip>
      )}
    </Container>
  );
};

const TreatmentFormatterCell = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const originalTreatment = cell.getValue<string>();
  const { treatmentAcceptanceStatus } = cell.row.original;

  return (
    <TreatmentFormatter
      originalTreatment={originalTreatment}
      treatmentAcceptanceStatus={treatmentAcceptanceStatus}
    />
  );
};

const VerificationFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const theme = useTheme();

  const value = String(
    cell.getValue() === "On_hold"
      ? translate.t("searchFindings.tabVuln.onHold")
      : (cell.getValue() ?? ""),
  );

  if (value === "") {
    return (
      <Text color={theme.palette.gray[400]} fontStyle={"italic"} size={"md"}>
        {translate.t("searchFindings.tabVuln.notRequested")}
      </Text>
    );
  }

  return <Text size={"md"}>{value}</Text>;
};

const AcceptanceFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const theme = useTheme();

  const value = String(cell.getValue() ?? "");

  if (value === "") {
    return (
      <Text color={theme.palette.gray[400]} fontStyle={"italic"} size={"md"}>
        {translate.t("searchFindings.tabVuln.notPending")}
      </Text>
    );
  }

  return <Text size={"md"}>{value}</Text>;
};

const TagsFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const theme = useTheme();

  const value = String(cell.getValue() ?? "");

  if (value === "") {
    return (
      <Text color={theme.palette.gray[400]} fontStyle={"italic"} size={"md"}>
        {translate.t("searchFindings.tabVuln.notProvided")}
      </Text>
    );
  }

  return <Text size={"md"}>{value}</Text>;
};

const ZeroRiskFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const status = cell.getValue<string>();
  const formatedStatus: string = capitalize(status);
  const currentStateVariant = getTagVariant(capitalize(status));
  const tagText =
    formatedStatus === "On_hold"
      ? t("searchFindings.tabVuln.onHold")
      : formatedStatus.split(" ")[0];

  if (formatedStatus === "") {
    return (
      <Text color={theme.palette.gray[400]} fontStyle={"italic"} size={"md"}>
        {translate.t("searchFindings.tabVuln.notApplied")}
      </Text>
    );
  }

  return (
    <Tag
      priority={currentStateVariant === "inactive" ? "default" : "low"}
      tagLabel={tagText}
      variant={currentStateVariant}
    />
  );
};

const TreatmentAssignedFormatter = (
  cell: CellContext<IVulnRowAttr, unknown>,
): JSX.Element | string => {
  const theme = useTheme();
  const vuln: IVulnRowAttr = cell.row.original;
  const { treatmentAssigned } = vuln;

  const text = typeof treatmentAssigned === "string" ? treatmentAssigned : "";

  if (text === "") {
    return (
      <Text color={theme.palette.gray[400]} fontStyle={"italic"} size={"md"}>
        {translate.t("searchFindings.tabVuln.notAssigned")}
      </Text>
    );
  }

  return (
    <Container {...{ "data-private": true }} display={"inline-block"}>
      <Container alignItems={"center"} display={"flex"} gap={0.25}>
        <Text color={"#212a36"} display={"inline-block"} size={"md"}>
          {text}
        </Text>
      </Container>
    </Container>
  );
};

export {
  AcceptanceFormatter,
  LocationFormatter,
  OriginFormatter,
  SpecificFormatter,
  StatusFormatter,
  SeverityFormatter,
  PriorityFormatter,
  TagsFormatter,
  TechniqueFormatter,
  TreatmentFormatter,
  TreatmentFormatterCell,
  ZeroRiskFormatter,
  TreatmentAssignedFormatter,
  VerificationFormatter,
};
